import Analysis from 'models/analysis';
import LayoutCheckbox from 'components/forms/layoutCheckbox';
import LayoutDatePicker from 'components/forms/layoutDatePicker';
import LayoutInput from 'components/forms/layoutInput';
import LayoutSelect from 'components/forms/layoutSelect';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {balanceColumns} from 'helpers/fieldHelpers';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import {bindLocaleUpdate} from 'helpers/bindLocaleUpdate';


class SourceTest extends React.Component {
  static defaultProps = {
    condition: true,
  };

  static propTypes = {
    analysis: PropTypes.instanceOf(Analysis).isRequired,
    condition: PropTypes.bool.isRequired,
    dataChanged: PropTypes.func.isRequired,
    edit: PropTypes.bool.isRequired,
    group: PropTypes.object.isRequired,
    pool: PropTypes.object.isRequired,
    poolChanged: PropTypes.func.isRequired,
    testers: PropTypes.array.isRequired,
    intl: intlShape.isRequired,
  };

  render() {
    let analysis = this.props.analysis;
    let analysisChanged = this.props.dataChanged;
    let pool = this.props.pool;
    let poolChanged = this.props.poolChanged;
    let topSection = [];

    let {formatMessage} = this.props.intl;

    topSection.push(<LayoutDatePicker value={analysis.modified ? moment(analysis.modified) : null}
                                      editMode={false}
                                      onChange={analysisChanged}
                                      dataKey='lastSource'
                                      key='lastSource'
                                      dateFormat='L'
                                      label={formatMessage({id: 'general.last.updated'})}/>);
    topSection.push(<LayoutSelect value={analysis.sourceWaterType}
                                  editMode={this.props.edit}
                                  dataChanged={analysisChanged}
                                  dataKey='sourceWaterType'
                                  key='sourceWaterType'
                                  keys={['City', 'Well', 'Other']}
                                  options={[
                                    formatMessage({id: 'general.city'}),
                                    formatMessage({id: 'general.well'}),
                                    formatMessage({id: 'general.other'}),
                                  ]}
                                  label={formatMessage({id: 'general.water.type'})}/>);
    topSection.push(<LayoutCheckbox value={pool.sourceConcern}
                                    editMode={this.props.edit}
                                    label={formatMessage({id: 'general.yes'})}
                                    hideLabel={!this.props.edit}
                                    dataChanged={poolChanged}
                                    dataKey='sourceConcern'
                                    key='sourceConcern'
                                    topLabel={formatMessage({id: 'source.source.concern'})}
                                    tooltip={formatMessage({id: 'source.source.tooltip'})}/>);

    let sourceFields = [];

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.tds'})}
                                   dataKey='tDS'
                                   key='tDS'
                                   value={analysis.tDS}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   group={this.props.group}
                                   validate={val => val >= 0 && val <= 9999}
                                   validateMessage={<FormattedMessage id='analysis.validate.dissolved.solids'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.ph'})}
                                   dataKey='pH'
                                   key='pH'
                                   value={analysis.pH}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   validate={val => (!!val || val === 0) && val >= 0.1 && val <= 14.0}
                                   validateMessage={<FormattedMessage id='source.validate.ph'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.copper'})}
                                   dataKey='copper'
                                   key='copper'
                                   value={analysis.copper}
                                   group={this.props.group}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   validate={val => (!!val || val === 0) >= 0 && val <= 99.9}
                                   validateMessage={<FormattedMessage id='source.validate.copper'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.total.chlorine'})}
                                   dataKey='totalChlorine'
                                   key='totalChlorine'
                                   group={this.props.group}
                                   value={analysis.totalChlorine}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   validate={val => val >= 0 && val <= 99.9 &&
                                     (!analysis.freeChlorine || parseFloat(val) >= parseFloat(analysis.freeChlorine))}
                                   validateMessage={<FormattedMessage id='analysis.validate.total.chlorine'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.total.alkalinity'})}
                                   dataKey='alkalinity'
                                   key='alkalinity'
                                   value={analysis.alkalinity}
                                   group={this.props.group}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   validate={val => val >= 0 && val <= 999}
                                   validateMessage={<FormattedMessage id='analysis.validate.total.alkalinity'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.iron'})}
                                   dataKey='iron'
                                   key='iron'
                                   value={analysis.iron}
                                   group={this.props.group}
                                   editMode={this.props.edit}
                                   dataChanged={analysisChanged}
                                   validate={val => (!!val || val === 0) >= 0 && val <= 99.9}
                                   validateMessage={<FormattedMessage id='source.validate.iron'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.free.chlorine'})}
                                   dataKey='freeChlorine'
                                   key='freeChlorine'
                                   value={analysis.freeChlorine}
                                   group={this.props.group}
                                   dataChanged={analysisChanged}
                                   editMode={this.props.edit}
                                   validate={val => analysis.chlorineDemandOnly || val >= 0 && val <= 999}
                                   validateMessage={<FormattedMessage id='source.validate.free.cl'/>}/>);

    sourceFields.push(<LayoutInput label={formatMessage({id: 'general.calcium.hardness'})}
                                   dataKey='hardness'
                                   key='hardness'
                                   value={analysis.hardness}
                                   dataChanged={analysisChanged}
                                   group={this.props.group}
                                   editMode={this.props.edit}
                                   validate={val => val >= 0 && val <= 9999}
                                   validateMessage={<FormattedMessage id='analysis.validate.calcium.hardness'/>}/>);

    sourceFields.push(<LayoutCheckbox topLabel={formatMessage({id: 'general.manganese'})}
                                      label={formatMessage({id: 'general.yes'})}
                                      dataKey='manganese'
                                      key='manganese'
                                      value={analysis.manganese}
                                      dataChanged={analysisChanged}
                                      editMode={this.props.edit}
                                      hideLabel={!this.props.edit}/>);

    let sourceColumns = balanceColumns(sourceFields, 3);

    let output =
      <div className='container source-water-test'>
        {balanceColumns(topSection, 3)}
        {this.props.edit || analysis.waterTester !== formatMessage({id: 'scanning.select.tester'}) ?
          <div className='row row-highlighted'>
            <div className='col-xs-4'>
              <LayoutSelect label={formatMessage({id: 'general.tester'})}
                            editMode={this.props.edit}
                            dataChanged={analysisChanged}
                            validateMessage={<FormattedMessage id='analysis.select.name.tester' />}
                            group={this.props.group}
                            required={true}
                            validate={value => value !== formatMessage({id: 'scanning.select.tester'})}
                            dataKey='waterTester'
                            options={this.props.testers.map(t => t.name)}
                            value={analysis.waterTester || formatMessage({id: 'scanning.select.tester'})}/>
            </div>
          </div> : null}
        {sourceColumns}
      </div>

    ;

    return this.props.condition ? output : null;
  }
}

export default bindLocaleUpdate(injectIntl(SourceTest));
